import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { TaskFormFields, type TaskFormValue } from "../components/TaskFormFields";
import { ApiError, type FamilyApi } from "../lib/api";
import type { ParentMember, SessionResponse, TaskOccurrence, TaskTemplate } from "../lib/types";

interface TaskEditorPageProps { api: FamilyApi; offline: boolean; session?: Extract<SessionResponse, { authenticated: true }> }
const emptyForm: TaskFormValue = { title: "", notes: "", assignee: "HOUSEHOLD", date: "", time: "", visibility: "PANEL_PUBLIC", priority: "NORMAL", points: "1" };

export function TaskEditorPage({ api, offline, session }: TaskEditorPageProps) {
  const { templateId } = useParams();
  const editing = Boolean(templateId);
  const navigate = useNavigate();
  const errorRef = useRef<HTMLDivElement>(null);
  const [members, setMembers] = useState<ParentMember[]>([]);
  const [template, setTemplate] = useState<TaskTemplate | null>(null);
  const [occurrence, setOccurrence] = useState<TaskOccurrence | null>(null);
  const child = session?.member.role === "CHILD";
  const [value, setValue] = useState<TaskFormValue>(() => child ? { ...emptyForm, assignee: session.member.memberId, points: "0" } : emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [titleInvalid, setTitleInvalid] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const memberResult = await api.getMembers();
        if (!active) return;
        setMembers(memberResult.data);
        if (templateId) {
          const [templates, occurrences] = await Promise.all([api.getTemplates(), api.getOccurrences()]);
          if (!active) return;
          const foundTemplate = templates.data.find((item) => item.templateId === templateId);
          const foundOccurrence = occurrences.data.find((item) => item.templateId === templateId);
          if (!foundTemplate || !foundOccurrence) throw new Error("missing task");
          setTemplate(foundTemplate); setOccurrence(foundOccurrence);
          setValue(formFromTemplate(foundTemplate));
        }
      } catch (caught) { if (active) setError(messageFor(caught, "找不到這筆待辦，請回清單重新整理。")); }
      finally { if (active) setLoading(false); }
    };
    void load();
    return () => { active = false; };
  }, [api, templateId]);

  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const validation = validateForm(value);
    setTitleInvalid(validation.titleInvalid);
    if (validation.message) { setError(validation.message); return; }
    setSaving(true); setError(null);
    const taskFields = {
      title: value.title.trim(), notes: value.notes.trim() || null,
      visibility: value.visibility, priority: value.priority,
      startDate: value.date || null, defaultDueTimeMinute: minuteOfDay(value.time),
    };
    const assignment = formAssignment(value.assignee, session);
    try {
      if (editing && template && occurrence) {
        await api.updateTemplate(template.templateId, { expectedVersion: template.version, expectedOccurrenceVersion: occurrence.version, scope: "ONLY_OCCURRENCE", ...taskFields, ...(child ? {} : { assignment, points: Number(value.points) }) });
      } else {
        await api.createTemplate({ ...taskFields, assignment, points: child ? 0 : Number(value.points), recurrence: { kind: "ONCE" } });
      }
      navigate("/tasks", { replace: true, state: { saved: true } });
    } catch (caught) { setError(messageFor(caught, "待辦沒有儲存，請再試一次。")); }
    finally { setSaving(false); }
  };

  return (
    <section className="page-stack editor-page" aria-labelledby="editor-title">
      <header className="page-heading"><Link className="back-link" to="/tasks">← 返回所有待辦</Link><h1 id="editor-title">{editing ? "編輯待辦" : "新增待辦"}</h1><p>{editing ? "修改會同步到手機與家中面板。" : "安排一件清楚、做得到的事。"}</p></header>
      {offline ? <p className="stale-note">目前離線，連線後才能儲存。</p> : null}
      {error ? <div ref={errorRef} className="notice notice-error" role="alert" tabIndex={-1}>{error}</div> : null}
      {loading ? <div className="form-skeleton" aria-busy="true" aria-label="正在載入待辦表單"><span /><span /><span /></div> : (
        <form onSubmit={(event) => void submit(event)} noValidate>
          <TaskFormFields value={value} members={members} disabled={saving} titleInvalid={titleInvalid} allowAssignment={!child} allowPoints={!child} onChange={setValue} />
          <div className="form-actions"><Link className="button button-ghost" to="/tasks">取消</Link><button className="button button-primary" type="submit" disabled={offline || saving}>{saving ? "儲存中…" : editing ? "儲存修改" : "新增待辦"}</button></div>
        </form>
      )}
    </section>
  );
}

function formFromTemplate(template: TaskTemplate): TaskFormValue {
  return { title: template.title, notes: template.notes ?? "", assignee: template.assignment.type === "HOUSEHOLD" ? "HOUSEHOLD" : template.assignment.memberId, date: template.startDate ?? "", time: minuteToTime(template.defaultDueTimeMinute), visibility: template.visibility, priority: template.priority, points: String(template.points) };
}
function formAssignment(
  assignee: string,
  session: Extract<SessionResponse, { authenticated: true }> | undefined,
) {
  if (session?.member.role === "CHILD") {
    return { type: "MEMBER" as const, memberId: session.member.memberId };
  }
  return assignee === "HOUSEHOLD"
    ? { type: "HOUSEHOLD" as const }
    : { type: "MEMBER" as const, memberId: assignee };
}
function validateForm(value: TaskFormValue) { const title = value.title.trim(); const points = Number(value.points); if (!title || Array.from(title).length > 120 || new TextEncoder().encode(title).length > 384) return { titleInvalid: true, message: "待辦名稱需為 1 到 120 個字。" }; if (!Number.isInteger(points) || points < 0 || points > 100) return { titleInvalid: false, message: "點數需為 0 到 100 的整數。" }; if (!value.date && value.time) return { titleInvalid: false, message: "設定時間前，請先選擇日期。" }; return { titleInvalid: false, message: null }; }
function minuteOfDay(time: string) { if (!time) return null; const [hours, minutes] = time.split(":").map(Number); return Number.isInteger(hours) && Number.isInteger(minutes) ? hours! * 60 + minutes! : null; }
function minuteToTime(minute: number | null) { if (minute === null) return ""; return `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`; }
function messageFor(error: unknown, fallback: string) { return error instanceof ApiError ? error.message : fallback; }
